import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Shield, Bug, Code, Search } from 'lucide-react';
import { Button } from './ui/button';

const Hero = ({ data }) => {
  const [currentTitle, setCurrentTitle] = useState(0);
  
  const titles = [
    'Offensive Security Engineer',
    'Penetration Tester',
    'Security Analyst',
    'Ethical Hacker'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTitle((prev) => (prev + 1) % titles.length);
    }, 2500);
    
    return () => clearInterval(interval);
  }, [titles.length]);

  const scrollToSkills = () => {
    document.getElementById('skills')?.scrollIntoView({ behavior: 'smooth' });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut' }
    }
  };

  return (
    <section id="hero" className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-black via-gray-900 to-black">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-10 -left-10 w-72 h-72 bg-green-400/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-10 -right-10 w-96 h-96 bg-cyan-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        {/* Matrix-style falling code effect */}
        <div className="absolute inset-0 opacity-5">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-green-400 font-mono text-xs"
              style={{
                left: `${(i * 5) % 100}%`,
                top: `-10px`
              }}
              animate={{
                y: ['0vh', '110vh'],
                opacity: [0, 1, 0]
              }}
              transition={{
                duration: 8 + (i % 4),
                repeat: Infinity,
                delay: i * 0.5,
                ease: 'linear'
              }}
            >
              {Array.from({ length: 20 }, () => Math.random() > 0.5 ? '1' : '0').join('')}
            </motion.div>
          ))}
        </div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="text-center max-w-4xl mx-auto"
        >
          {/* Main Title */}
          <motion.div variants={itemVariants} className="mb-6">
            <div className="flex items-center justify-center mb-4">
              <Shield className="w-12 h-12 text-green-400 mr-4" />
              <div className="h-px bg-gradient-to-r from-transparent via-green-400 to-transparent flex-1 mx-4" />
              <div className="flex space-x-2">
                <Bug className="w-6 h-6 text-red-400" />
                <Code className="w-6 h-6 text-cyan-400" />
                <Search className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white via-green-400 to-cyan-400">
              {data.name}
            </h1>
          </motion.div>

          {/* Dynamic Title */}
          <motion.div 
            variants={itemVariants}
            className="mb-8 h-20 flex items-center justify-center"
          >
            <motion.h2
              key={currentTitle}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-2xl md:text-4xl font-semibold text-green-400 font-mono"
            >
              &gt; {titles[currentTitle]}_
            </motion.h2>
          </motion.div>

          {/* Tagline */}
          <motion.p 
            variants={itemVariants}
            className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed"
          >
            {data.tagline}
          </motion.p>

          {/* Stats */}
          <motion.div 
            variants={itemVariants}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 max-w-3xl mx-auto"
          >
            {data.stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center p-4 bg-gray-900/50 rounded-lg border border-gray-700 backdrop-blur-sm"
                whileHover={{ scale: 1.05, borderColor: '#10b981' }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <div className="text-2xl md:text-3xl font-bold text-green-400 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-400 uppercase tracking-wider">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div 
            variants={itemVariants}
            className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6"
          >
            <Button
              onClick={scrollToSkills}
              className="bg-green-500 hover:bg-green-600 text-black font-semibold px-8 py-3 text-lg transition-all duration-300 transform hover:scale-105"
            >
              Explore Skills
            </Button>
            
            <Button
              variant="outline"
              onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
              className="border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black font-semibold px-8 py-3 text-lg transition-all duration-300 transform hover:scale-105"
            >
              View Projects
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <ChevronDown className="w-6 h-6 text-green-400 cursor-pointer" onClick={scrollToSkills} />
      </motion.div>
    </section>
  );
};

export default Hero;